import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Card,
  Divider,
  useTheme,
  useMediaQuery,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { 
  Calculate as CalculateIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import { store } from '../store';
import { setMonthlyReportGeneratePreference } from '../store/slices/monthlyReportGeneratePreferenceSlice';
import { setMonthlyAttendanceSummaryDateRange } from '../store/slices/monthlyAttendanceSummaryDateRangeSlice';
import Swal from 'sweetalert2';
import { useAuth } from '../contexts/AuthContext';
import { useCanManageMonthlyRecords } from '../utils/shiftRoles';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

// 配置 dayjs 時區插件
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Hong_Kong');

const EN_WEEK_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ZH_WEEK_SHORT = ['日', '一', '二', '三', '四', '五', '六'];

/** 日期底下顯示星期：英文 (Mon)、中文 (一)，按 Asia/Hong_Kong */
function formatWeekdayShortParen(dateStr, language) {
  if (!dateStr) return '';
  const d = dayjs(dateStr).tz('Asia/Hong_Kong');
  if (!d.isValid()) return '';
  const dow = d.day();
  if (language && String(language).toLowerCase().startsWith('en')) {
    return `(${EN_WEEK_SHORT[dow]})`;
  }
  return `(${ZH_WEEK_SHORT[dow]})`;
}

/** 與月結表主表格「考勤情況」欄一致的有效打卡記錄（供總計次數用） */
function getValidClockRecordsForMonthlySummary(day) {
  if (day.valid_clock_records && Array.isArray(day.valid_clock_records) && day.valid_clock_records.length > 0) {
    return day.valid_clock_records;
  }
  if (day.attendance_data?.clock_records && Array.isArray(day.attendance_data.clock_records)) {
    return day.attendance_data.clock_records
      .filter((r) => {
        const isValid =
          r.is_valid === true ||
          r.is_valid === 'true' ||
          r.is_valid === 1 ||
          r.is_valid === '1' ||
          r.is_valid === 'True' ||
          (typeof r.is_valid === 'string' && r.is_valid.toLowerCase() === 'true');
        return isValid && r.clock_time;
      })
      .sort((a, b) => {
        const timeA = a.clock_time || '';
        const timeB = b.clock_time || '';
        return timeA.localeCompare(timeB);
      });
  }
  return [];
}

function computeMonthlySummaryFootStats(dailyData) {
  return dailyData.reduce(
    (acc, day) => {
      acc.late_minutes += day.late_minutes || 0;
      acc.break_duration += day.break_duration || 0;
      acc.total_work_hours += day.total_work_hours || 0;
      acc.overtime_hours += day.overtime_hours || 0;
      acc.approved_overtime_minutes += day.approved_overtime_minutes || 0;

      const schedule = day.schedule || day.attendance_data?.schedule;
      if (schedule) acc.schedule_day_count += 1;
      if (day.store_short_name) acc.store_day_count += 1;
      if (getValidClockRecordsForMonthlySummary(day).length > 0) acc.attendance_day_count += 1;
      if ((day.late_minutes ?? 0) > 0) acc.late_occurrence_count += 1;

      return acc;
    },
    {
      late_minutes: 0,
      break_duration: 0,
      total_work_hours: 0,
      overtime_hours: 0,
      approved_overtime_minutes: 0,
      schedule_day_count: 0,
      store_day_count: 0,
      attendance_day_count: 0,
      late_occurrence_count: 0
    }
  );
}

/** 從 Redux 還原月結表日期區間；無效則本月首日～今日（香港） */
function getInitialDateRangeForMonthlySummary() {
  try {
    const { start_date, end_date } = store.getState().monthlyAttendanceSummaryDateRange;
    const ymd = /^\d{4}-\d{2}-\d{2}$/;
    if (!start_date || !end_date || !ymd.test(start_date) || !ymd.test(end_date)) {
      const hk = dayjs().tz('Asia/Hong_Kong');
      return { start: hk.startOf('month'), end: hk.startOf('day') };
    }
    const [ys, ms, ds] = start_date.split('-').map(Number);
    const [ye, me, de] = end_date.split('-').map(Number);
    const s = dayjs.tz({ year: ys, month: ms - 1, day: ds }, 'Asia/Hong_Kong').startOf('day');
    const e = dayjs.tz({ year: ye, month: me - 1, day: de }, 'Asia/Hong_Kong').startOf('day');
    if (!s.isValid() || !e.isValid() || s.isAfter(e, 'day')) {
      const hk = dayjs().tz('Asia/Hong_Kong');
      return { start: hk.startOf('month'), end: hk.startOf('day') };
    }
    const days = e.diff(s, 'day') + 1;
    if (days < 1 || days > 45) {
      const hk = dayjs().tz('Asia/Hong_Kong');
      return { start: hk.startOf('month'), end: hk.startOf('day') };
    }
    return { start: s, end: e };
  } catch {
    const hk = dayjs().tz('Asia/Hong_Kong');
    return { start: hk.startOf('month'), end: hk.startOf('day') };
  }
}

const MonthlyAttendanceSummary = ({ noLayout = false }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canManageMonthly = useCanManageMonthlyRecords(user);
  const dispatch = useDispatch();
  const monthlyReportGeneratePreference = useSelector((state) => state.monthlyReportGeneratePreference);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedUserId, setSelectedUserId] = useState(null);
  // 初始：Redux 暫存區間；無則本月首日～今日（最多 45 日）
  const [selectedStartDate, setSelectedStartDate] = useState(
    () => getInitialDateRangeForMonthlySummary().start
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    () => getInitialDateRangeForMonthlySummary().end
  );
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [calculateDialogOpen, setCalculateDialogOpen] = useState(false);
  const [calculatingDate, setCalculatingDate] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDayDetail, setSelectedDayDetail] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [generateReportDialogOpen, setGenerateReportDialogOpen] = useState(false);
  const [dialogReportYear, setDialogReportYear] = useState(() => dayjs().tz('Asia/Hong_Kong').year());
  const [dialogReportMonth, setDialogReportMonth] = useState(() => dayjs().tz('Asia/Hong_Kong').month() + 1);
  const [stores, setStores] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await axios.get('/api/stores');
        setStores(response.data.stores || []);
      } catch (error) {
        console.error('Fetch stores error:', error);
      }
    };
    fetchStores();
  }, []);

  // 從 URL 參數中獲取 employee_number 並設置 selectedUserId
  useEffect(() => {
    const employeeNumber = searchParams.get('employee_number');
    if (employeeNumber && users.length > 0) {
      const foundUser = users.find(u => u.employee_number === employeeNumber);
      if (foundUser) {
        setSelectedUserId(foundUser.id);
      }
    }
  }, [searchParams, users]);

  // 開始／結束日期寫入 Redux（登出時清除）
  useEffect(() => {
    if (!selectedStartDate || !selectedEndDate) return;
    dispatch(
      setMonthlyAttendanceSummaryDateRange({
        start_date: selectedStartDate.tz('Asia/Hong_Kong').format('YYYY-MM-DD'),
        end_date: selectedEndDate.tz('Asia/Hong_Kong').format('YYYY-MM-DD')
      })
    );
  }, [selectedStartDate, selectedEndDate, dispatch]);

  // 當選了員工/年/月 或 進入本頁（pathname）時重新拉取，確保從 /schedule 改完編更回來會拿到最新 daily_data
  useEffect(() => {
    if (selectedUserId && selectedStartDate && selectedEndDate) {
      fetchSummary();
    }
  }, [selectedUserId, selectedStartDate, selectedEndDate, location.pathname]);

  // 當用戶從其他分頁返回本頁時重新拉取（例如在另一分頁改完編更後返回）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && selectedUserId && selectedStartDate && selectedEndDate) {
        fetchSummary();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedUserId, selectedStartDate, selectedEndDate]);

  const fetchUsers = async () => {
    try {
      // 使用新的 API 端點，允許批核者訪問
      const response = await axios.get('/api/users/list');
      const usersList = response.data.users || [];
      // 按 employee_number 排序
      usersList.sort((a, b) => {
        const aNum = a.employee_number || '';
        const bNum = b.employee_number || '';
        return aNum.localeCompare(bNum, undefined, { numeric: true, sensitivity: 'base' });
      });
      setUsers(usersList);
    } catch (error) {
      console.error('Fetch users error:', error);
      Swal.fire({
        icon: 'error',
        title: t('attendance.error') || '錯誤',
        text: error.response?.data?.message || '獲取用戶列表失敗'
      });
    }
  };

  // 解析時間字符串（HH:mm 或 HH:mm:ss）並返回分鐘數（從當天00:00開始）
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    try {
      const timeOnly = timeStr.split(':').slice(0, 2).join(':');
      const [hours, minutes] = timeOnly.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) return null;
      return hours * 60 + minutes;
    } catch (error) {
      return null;
    }
  };

  // 將分鐘數向下取整到指定間隔（例如 30 / 15）
  const floorMinutesToInterval = (minutes, interval) => {
    if (minutes === null || minutes === undefined) return null;
    const m = typeof minutes === 'string' ? Number(minutes) : minutes;
    if (Number.isNaN(m)) return null;
    if (!interval || interval <= 0) return Math.floor(m);
    return Math.floor(m / interval) * interval;
  };

  const getInclusiveDayCount = (start, end) => {
    if (!start || !end) return 0;
    return end.startOf('day').diff(start.startOf('day'), 'day') + 1;
  };

  const handleStartDateChange = (newValue) => {
    if (!newValue) return;
    const nextStart = newValue.tz('Asia/Hong_Kong').startOf('day');
    setSelectedStartDate(nextStart);

    // 若起始日期晚於目前結束日期：先把結束日期同步到起始日期，再做 45 天檢查
    const effectiveEnd = (selectedEndDate && nextStart.isAfter(selectedEndDate, 'day'))
      ? nextStart
      : selectedEndDate;

    if (effectiveEnd) {
      // 最長45日（含起訖）
      if (getInclusiveDayCount(nextStart, effectiveEnd) > 45) {
        setSelectedEndDate(nextStart.add(44, 'day').startOf('day'));
      } else if (nextStart.isAfter(effectiveEnd, 'day')) {
        setSelectedEndDate(nextStart);
      }
    }
  };

  const handleEndDateChange = (newValue) => {
    if (!newValue) return;

    let nextEnd = newValue.tz('Asia/Hong_Kong').startOf('day');

    if (selectedStartDate && nextEnd.isBefore(selectedStartDate, 'day')) {
      nextEnd = selectedStartDate;
    }

    // 最長45日（含起訖）
    if (selectedStartDate && getInclusiveDayCount(selectedStartDate, nextEnd) > 45) {
      const clamped = selectedStartDate.add(44, 'day').startOf('day');
      Swal.fire({
        icon: 'warning',
        title: '日期區間最多45天',
        text: '已自動調整結束日期至符合限制。'
      });
      nextEnd = clamped;
    }

    setSelectedEndDate(nextEnd);
  };

  // 計算一天的考勤數據
  const calculateDailyAttendance = (attendanceItem, storesMap, employmentMode) => {
    const dateStr = attendanceItem.attendance_date;
    const schedule = attendanceItem.schedule || null;
    const clockRecords = attendanceItem.clock_records || [];
    
    // 獲取有效的打卡記錄（按時間排序）
    const validRecords = clockRecords
      .filter(r => {
        const isValid = r.is_valid === true || 
                      r.is_valid === 'true' || 
                      r.is_valid === 1 || 
                      r.is_valid === '1' ||
                      r.is_valid === 'True' ||
                      (typeof r.is_valid === 'string' && r.is_valid.toLowerCase() === 'true');
        return isValid && r.clock_time;
      })
      .sort((a, b) => {
        const timeA = a.clock_time || '';
        const timeB = b.clock_time || '';
        return timeA.localeCompare(timeB);
      });

    const result = {
      date: dateStr,
      late_minutes: null,
      break_duration: null,
      total_work_hours: null,
      overtime_hours: null,
      approved_overtime_minutes: null,
      early_leave: false,
      is_late: false,
      is_absent: false,
      store_short_name: null,
      schedule: schedule && (schedule.start_time || schedule.end_time || schedule.leave_type_name_zh) ? {
        id: schedule.id || null,
        store_id: schedule.store_id || null,
        start_time: schedule.start_time || null,
        end_time: schedule.end_time || null,
        leave_type_name_zh: schedule.leave_type_name_zh || null,
        leave_type_name: schedule.leave_type_name || null,
        leave_type_code: schedule.leave_type_code || null,
        leave_session: schedule.leave_session || null,
        is_approved_leave: schedule.is_approved_leave || false
      } : null,
      valid_clock_records: validRecords.map(record => ({
        id: record.id,
        employee_number: record.employee_number || null,
        name: record.name || null,
        branch_code: record.branch_code || null,
        attendance_date: record.attendance_date || null,
        clock_time: record.clock_time || null,
        in_out: record.in_out || null,
        is_valid: true,
        remarks: record.remarks || null
      })),
      attendance_data: {
        attendance_date: dateStr,
        clock_records: clockRecords,
        schedule: schedule
      }
    };

    // 根據第一個有效打卡記錄的 branch_code 查找 store_short_name
    if (validRecords.length > 0 && validRecords[0].branch_code) {
      const storeCode = String(validRecords[0].branch_code).trim();
      if (storeCode && storesMap && storesMap[storeCode]) {
        result.store_short_name = storesMap[storeCode];
      } else if (storeCode) {
        console.warn(`[calculateDailyAttendance] Date ${dateStr}: No store found for store_code "${storeCode}"`);
      }
    }

    if (validRecords.length === 0) {
      // 沒有有效打卡記錄，判斷是否缺勤
      if (schedule && schedule.start_time) {
        result.is_absent = true;
      }
      return result;
    }

    // 獲取排班時間
    const scheduleStartTime = schedule?.start_time;
    const scheduleEndTime = schedule?.end_time;

    // 第一個有效記錄作為上班時間
    const clockInTime = validRecords[0]?.clock_time;
    // 最後一個有效記錄作為下班時間
    const clockOutTime = validRecords[validRecords.length - 1]?.clock_time;

    // 計算遲到
    if (scheduleStartTime && clockInTime) {
      const scheduleStart = parseTime(scheduleStartTime);
      const actualStart = parseTime(clockInTime);
      
      if (actualStart !== null && scheduleStart !== null && actualStart > scheduleStart) {
        const diffMinutes = actualStart - scheduleStart;
        result.late_minutes = diffMinutes;
        result.is_late = true;
      }
    }

    // 計算Break時間（第三個有效時間減去第二個有效時間）
    if (validRecords.length >= 3) {
      const secondTime = parseTime(validRecords[1]?.clock_time);
      const thirdTime = parseTime(validRecords[2]?.clock_time);
      
      if (secondTime !== null && thirdTime !== null) {
        const breakMinutes = thirdTime - secondTime;
        result.break_duration = breakMinutes;
      }
    }

    // 計算總工作時數
    // 全日上班總時數 = 打卡最後時間 - 排班開始時間（如果有排班時間）
    // 如果沒有排班時間，則 = 打卡最後時間 - 當日首次打卡時間
    let workMinutes = null;
    if (clockOutTime) {
      const end = parseTime(clockOutTime);
      
      if (end !== null) {
        let start = null;
        
        // 優先使用排班開始時間
        if (scheduleStartTime) {
          start = parseTime(scheduleStartTime);
        } 
        // 如果沒有排班開始時間，使用首次打卡時間
        else if (clockInTime) {
          start = parseTime(clockInTime);
        }
        
        if (start !== null) {
          workMinutes = end - start;
          // 如果有遲到時間，需要減去
          if (result.late_minutes !== null && result.late_minutes > 0) {
            workMinutes -= result.late_minutes;
          }
          result.total_work_hours = workMinutes / 60;
        }
      }
    }

    // 計算超時工作時間
    let overtimeMinutes = null;
    const mode = (employmentMode || '').toString().trim().toUpperCase();
    if (mode === 'FT') {
      // FT 員工：超時工作時間以「全日上班總時數」計算，超過 9 小時就開始計
      if (workMinutes !== null && workMinutes !== undefined) {
        const thresholdMinutes = 9 * 60;
        const ot = workMinutes - thresholdMinutes;
        if (ot > 0) {
          overtimeMinutes = ot;
          result.overtime_hours = overtimeMinutes / 60;
        }
      }
    } else {
      // 其他：維持原本邏輯（按排班下班時間計）
      if (scheduleEndTime && clockOutTime) {
        const scheduleEnd = parseTime(scheduleEndTime);
        const actualEnd = parseTime(clockOutTime);
        
        if (scheduleEnd !== null && actualEnd !== null && actualEnd > scheduleEnd) {
          overtimeMinutes = actualEnd - scheduleEnd;
          if (overtimeMinutes >= 15) {
            result.overtime_hours = overtimeMinutes / 60;
          }
        }
      }
    }

    // 計算應計工作時數
    if (mode === 'PT') {
      // PT員工：應計工作時數 = 全日上班總時數，向下取整到15分鐘
      if (result.total_work_hours !== null && result.total_work_hours !== undefined) {
        const totalWorkMinutes = result.total_work_hours * 60; // 將小時轉換為分鐘
        result.approved_overtime_minutes = floorMinutesToInterval(totalWorkMinutes, 15);
      }
    } else {
      // FT員工：應計超時工作時數按超時工作時間向下取整到30分鐘（超過 9 小時先開始計）
      if (overtimeMinutes !== null && overtimeMinutes > 0) {
        result.approved_overtime_minutes = floorMinutesToInterval(overtimeMinutes, 30);
      }
    }

    // 計算早退
    if (scheduleEndTime && clockOutTime) {
      const scheduleEnd = parseTime(scheduleEndTime);
      const actualEnd = parseTime(clockOutTime);
      
      if (scheduleEnd !== null && actualEnd !== null && actualEnd < scheduleEnd) {
        result.early_leave = true;
      }
    }

    return result;
  };

  const fetchSummary = async () => {
    if (!selectedUserId) return;

    setLoading(true);
    setSummary(null); // 每次取數前清空，不顯示上一筆員工的資料
    try {
      // 每次從後端即時取得月結表資料（排班、例假、已批核假期、考勤等）
      const listResponse = await axios.get('/api/monthly-attendance-summaries', {
        params: {
          user_id: selectedUserId,
          start_date: selectedStartDate.tz('Asia/Hong_Kong').format('YYYY-MM-DD'),
          end_date: selectedEndDate.tz('Asia/Hong_Kong').format('YYYY-MM-DD'),
          // 仍保留 year/month 方便後端維持相容（後端會以 start_date/end_date 為準）
          year: selectedStartDate.tz('Asia/Hong_Kong').year(),
          month: selectedStartDate.tz('Asia/Hong_Kong').month() + 1
        }
      });

      let summaries = listResponse.data?.summaries || [];
      let summaryData = summaries.length > 0 ? summaries[0] : null;
      setSummary(summaryData);
    } catch (error) {
      console.error('Fetch summary error:', error);
      Swal.fire({
        icon: 'error',
        title: t('attendance.error') || '錯誤',
        text: error.response?.data?.message || '取得月結記錄失敗'
      });
    } finally {
      setLoading(false);
    }
  };

  const reportYearOptions = useMemo(() => {
    const hkNow = dayjs().tz('Asia/Hong_Kong');
    const maxY = hkNow.year() + 1;
    const minY = 2020;
    const list = [];
    for (let y = minY; y <= maxY; y += 1) list.push(y);
    return list;
  }, []);

  const openGenerateReportDialog = () => {
    if (!selectedUserId || !selectedStartDate || !selectedEndDate) {
      Swal.fire({
        icon: 'warning',
        title: t('attendance.warning'),
        text: '請選擇員工與日期區間（最多45天）'
      });
      return;
    }
    const hk = selectedStartDate.tz('Asia/Hong_Kong');
    const prefY = monthlyReportGeneratePreference.year;
    const prefM = monthlyReportGeneratePreference.month;
    const minY = reportYearOptions[0];
    const maxY = reportYearOptions[reportYearOptions.length - 1];
    let y = prefY != null ? prefY : hk.year();
    y = Math.min(maxY, Math.max(minY, y));
    let m = prefM != null ? prefM : hk.month() + 1;
    m = Math.min(12, Math.max(1, m));
    setDialogReportYear(y);
    setDialogReportMonth(m);
    setGenerateReportDialogOpen(true);
  };

  const performGenerateReport = async (year, month) => {
    if (!selectedUserId) {
      Swal.fire({
        icon: 'warning',
        title: t('attendance.warning'),
        text: '請選擇員工與日期區間（最多45天）'
      });
      return;
    }

    const y = Number(year);
    const mo = Number(month);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12) {
      Swal.fire({
        icon: 'error',
        title: t('attendance.error'),
        text: t('attendance.generateReportFailed')
      });
      return;
    }

    if (!selectedStartDate || !selectedEndDate) {
      Swal.fire({
        icon: 'warning',
        title: t('attendance.warning'),
        text: '請選擇員工與日期區間（最多45天）'
      });
      return;
    }

    const hkStart = selectedStartDate.tz('Asia/Hong_Kong').startOf('day');
    const hkEnd = selectedEndDate.tz('Asia/Hong_Kong').startOf('day');
    if (hkStart.isAfter(hkEnd, 'day')) {
      Swal.fire({
        icon: 'error',
        title: t('attendance.error'),
        text: t('attendance.generateReportFailed')
      });
      return;
    }
    const rangeDays = hkEnd.diff(hkStart, 'day') + 1;
    if (rangeDays > 45) {
      Swal.fire({
        icon: 'warning',
        title: t('attendance.warning'),
        text: '日期區間最多45天'
      });
      return;
    }

    // 月報掛靠年／月 = 對話框；統計資料來源 = 畫面上開始／結束日期（可與掛靠月份不一致）
    const start_date = hkStart.format('YYYY-MM-DD');
    const end_date = hkEnd.format('YYYY-MM-DD');

    try {
      setGeneratingReport(true);

      Swal.fire({
        title: t('attendance.generating'),
        text: t('attendance.generating'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await axios.post('/api/monthly-attendance-reports/generate', {
        user_id: selectedUserId,
        year: y,
        month: mo,
        start_date,
        end_date
      });

      Swal.fire({
        icon: 'success',
        title: t('attendance.success'),
        text: response.data.message || t('attendance.reportGenerated')
      });

      const reportId = response.data.report?.id;
      if (reportId) {
        navigate(`/monthly-attendance-report/${reportId}`);
      } else {
        await fetchSummary();
      }
    } catch (error) {
      console.error('Generate report error:', error);
      Swal.fire({
        icon: 'error',
        title: t('attendance.error'),
        text: error.response?.data?.message || t('attendance.generateReportFailed')
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleConfirmGenerateReportDialog = async () => {
    dispatch(
      setMonthlyReportGeneratePreference({
        year: dialogReportYear,
        month: dialogReportMonth
      })
    );
    setGenerateReportDialogOpen(false);
    await performGenerateReport(dialogReportYear, dialogReportMonth);
  };

  const handleCalculateDay = async (date) => {
    if (!selectedUserId) return;

    setCalculatingDate(date);
    setCalculateDialogOpen(true);

    try {
      // 獲取該日期的考勤數據（使用 UTC+8 時區）
      const dateStr = typeof date === 'string' 
        ? date 
        : dayjs(date).tz('Asia/Hong_Kong').format('YYYY-MM-DD');
      const [year, month, day] = dateStr.split('-').map(Number);

      // 獲取該日期的考勤數據並計算
      const dayData = summary?.daily_data?.find(d => d.date === dateStr);
      const response = await axios.post('/api/monthly-attendance-summaries/calculate-day', {
        user_id: selectedUserId,
        date: dateStr,
        attendance_data: dayData?.attendance_data || {},
        schedule_data: dayData?.schedule || dayData?.attendance_data?.schedule || {}
      });

      await fetchSummary();
      Swal.fire({
        icon: 'success',
        title: t('attendance.success') || '成功',
        text: t('attendance.calculateComplete') || '計算完成'
      });
    } catch (error) {
      console.error('Calculate day error:', error);
      Swal.fire({
        icon: 'error',
        title: t('attendance.error') || '錯誤',
        text: error.response?.data?.message || t('attendance.calculateFailed') || '計算失敗'
      });
    } finally {
      setCalculateDialogOpen(false);
      setCalculatingDate(null);
    }
  };

  const formatTime = (minutes) => {
    if (minutes === null || minutes === undefined) return '--';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${String(mins).padStart(2, '0')}`;
  };

  // 將小時數（可為小數）轉換成 hh:mm 顯示
  const formatHoursAsTime = (hours) => {
    if (hours === null || hours === undefined) return '--';
    const num = typeof hours === 'string' ? Number(hours) : hours;
    if (Number.isNaN(num)) return '--';
    const minutes = Math.round(num * 60);
    return formatTime(minutes);
  };

  const formatHours = (hours) => {
    if (hours === null || hours === undefined) return '--';
    return typeof hours === 'string' ? hours : hours.toFixed(2);
  };

  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return '--:--';
    // 如果是 HH:mm:ss 格式，只取前5個字符
    return timeStr.substring(0, 5);
  };

  const handleToggleRow = (date) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedRows(newExpanded);
  };

  const handleViewDetail = (day) => {
    setSelectedDayDetail(day);
    setDetailDialogOpen(true);
  };

  const handleExportPDF = async () => {
    if (!summary || !selectedUserId) {
      Swal.fire({
        icon: 'warning',
        title: t('attendance.error') || '錯誤',
        text: t('attendance.noMonthlySummary') || '沒有月結記錄'
      });
      return;
    }

    try {
      const selectedUser = users.find(u => u.id === selectedUserId);
      const userName = selectedUser 
        ? `${selectedUser.employee_number} - ${selectedUser.display_name || selectedUser.name_zh || selectedUser.name || ''}`
        : '';

      // 創建 PDF - 改為縱向（portrait）
      const doc = new jsPDF('portrait', 'mm', 'a4');
      
      // 使用默認字體（helvetica）避免字體相關錯誤
      // 注意：jsPDF 默認字體不支持中文，所以我們使用英文標籤
      // 如果需要支持中文，需要正確加載和註冊中文字體文件
      doc.setFont('helvetica', 'normal');
      
      // 標題 - 使用英文避免字體問題
      doc.setFontSize(18);
      doc.text('Monthly Attendance Summary', 14, 15);
      
      // 員工信息 - 使用英文避免字體問題，包含 display_name
      doc.setFontSize(12);
      const employeeDisplayName = selectedUser?.display_name || selectedUser?.name_zh || selectedUser?.name || '';
      doc.text(`Employee: ${selectedUser?.employee_number || ''} - ${employeeDisplayName}`, 14, 22);
      doc.text(`From: ${selectedStartDate.tz('Asia/Hong_Kong').format('YYYY-MM-DD')}`, 14, 28);
      doc.text(`To: ${selectedEndDate.tz('Asia/Hong_Kong').format('YYYY-MM-DD')}`, 14, 34);

    // 準備表格數據 - 使用英文避免字體問題
    // 注意：PDF 使用英文，避免中文顯示成亂碼（jsPDF 預設字體不支援中文）
    const tableData = dailyData.map(day => {
      const schedule = day.schedule || day.attendance_data?.schedule || null;
      const sessionSuffix = schedule?.leave_session ? ` (${schedule.leave_session})` : '';
      const rosterText = (schedule?.start_time || schedule?.end_time)
        ? `${schedule?.start_time ? formatTimeDisplay(schedule.start_time) : '--:--'} - ${schedule?.end_time ? formatTimeDisplay(schedule.end_time) : '--:--'}`
        : (schedule?.leave_type_code || schedule?.leave_type_name || schedule?.leave_type_name_zh
            ? (schedule.leave_type_code || schedule.leave_type_name || 'Leave') + sessionSuffix
            : '--');

      const storeText = day.store_short_name || '--';

      const validRecords = Array.isArray(day.valid_clock_records) ? day.valid_clock_records : [];
      const clockTimesText = validRecords.length > 0
        ? validRecords
            .filter(r => r?.clock_time)
            .map(r => formatTimeDisplay(r.clock_time))
            .join(' | ')
        : '--';

      return [
        day.date || '--',
        rosterText,
        storeText,
        clockTimesText,
        day.late_minutes !== null ? formatTime(day.late_minutes) : '--',
        day.break_duration !== null ? formatTime(day.break_duration) : '--',
        formatHoursAsTime(day.total_work_hours),
        formatHoursAsTime(day.overtime_hours),
        day.approved_overtime_minutes !== null ? formatTime(day.approved_overtime_minutes) : '--'
      ];
    });

    const footStats = computeMonthlySummaryFootStats(dailyData);
    const {
      late_minutes: lateTotal,
      break_duration: breakTotal,
      total_work_hours: workTotal,
      overtime_hours: otTotal,
      approved_overtime_minutes: approvedOtTotal,
      schedule_day_count: pdfScheduleCount,
      store_day_count: pdfStoreCount,
      attendance_day_count: pdfAttendanceCount,
      late_occurrence_count: pdfLateCount
    } = footStats;

    const lateFootCell =
      lateTotal > 0
        ? `${formatTime(lateTotal)}${pdfLateCount > 0 ? ` (${pdfLateCount} times)` : ''}`
        : '--';

    // 準備總計行（PDF 用英文，與表頭一致）
    const totalRow = [
      'Total',
      `${pdfScheduleCount} days`,
      `${pdfStoreCount} days`,
      `${pdfAttendanceCount} days`,
      lateFootCell,
      breakTotal > 0 ? formatTime(breakTotal) : '--',
      formatHoursAsTime(workTotal),
      formatHoursAsTime(otTotal),
      approvedOtTotal > 0 ? formatTime(approvedOtTotal) : '--'
    ];

    // 表格標題 - 使用英文避免字體問題（需與頁面 table 欄位一致）
    // 根據 employment_mode 決定標題
    const employmentMode = selectedUser?.position_employment_mode;
    const mode = (employmentMode || '').toString().trim().toUpperCase();
    const approvedOTLabel = mode === 'FT' ? 'Accrued OT Hours' : 'Accrued Work Hours';
    
    const tableHeaders = [
      'Date',
      'Roster',
      'Store',
      'Clock Times',
      'Late',
      'Break',
      'Work',
      'Overtime',
      approvedOTLabel
    ];

    // 確保有數據才生成表格
    let finalY = 40;
    if (tableData.length === 0) {
      doc.text('No data available', 14, 50);
      finalY = 50;
    } else {
      // 添加表格 - 不設置自定義字體，使用默認字體
      // 縱向模式下需要調整列寬，總寬度約為 180mm（A4 縱向寬度減去邊距）
      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        foot: [totalRow],
        startY: 40,
        styles: { 
          fontSize: 7,
          overflow: 'linebreak',
          cellPadding: 1.5
        },
        headStyles: { 
          fillColor: [25, 118, 210], 
          textColor: 255, 
          fontStyle: 'bold',
          overflow: 'linebreak',
          fontSize: 7
        },
        footStyles: {
          fillColor: [200, 200, 200],
          textColor: 0,
          fontStyle: 'bold',
          fontSize: 7
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 40, left: 14, right: 14 },
        columnStyles: {
          0: { cellWidth: 18 }, // Date
          1: { cellWidth: 22 }, // Roster
          2: { cellWidth: 20 }, // Store
          3: { cellWidth: 40 }, // Clock Times
          4: { cellWidth: 15 }, // Late
          5: { cellWidth: 15 }, // Break
          6: { cellWidth: 18 }, // Work
          7: { cellWidth: 18 },  // Overtime
          8: { cellWidth: 20 }  // Approved OT
        },
        didDrawPage: (data) => {
          finalY = data.cursor.y;
        }
      });
      
      // 獲取表格結束位置
      const tableEndY = doc.lastAutoTable.finalY || finalY;
      finalY = tableEndY + 10;
    }

    // 添加統計信息
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary:', 14, finalY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    finalY += 7;
    
    // 應計工作時數/應計超時工作時數
    doc.text(`${approvedOTLabel}: ${approvedOtTotal > 0 ? formatTime(approvedOtTotal) : '--'}`, 14, finalY);
    finalY += 6;
    
    // 總遲到分鐘數（只有 >= 10 分鐘時才顯示）
    if (lateTotal >= 10) {
      doc.text(
        `Total Late Minutes: ${formatTime(lateTotal)}${pdfLateCount > 0 ? ` (${pdfLateCount} times)` : ''}`,
        14,
        finalY
      );
      finalY += 6;
    }

    // 勤工獎資格檢查（僅對FT員工）
    if (mode === 'FT') {
      // 定義不符合資格的假期類型
      const disqualifiedLeaveTypes = [
        '無薪事假',
        '無薪病假',
        '病假 (疾病津貼)',
        '全薪病假',
        '工傷病假',
        '產假',
        '侍產假',
        '恩恤假'
      ];
      
      // 檢查是否有不符合資格的假期
      const hasDisqualifiedLeave = dailyData.some(day => {
        const leaveType = day.schedule?.leave_type_name_zh || 
                         day.attendance_data?.schedule?.leave_type_name_zh;
        return leaveType && disqualifiedLeaveTypes.includes(leaveType);
      });
      
      // 如果遲到 >= 10分鐘或有不符合資格的假期，顯示提示
      if (lateTotal >= 10 || hasDisqualifiedLeave) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 0, 0); // 紅色
        doc.setFontSize(9);
        doc.text('Not eligible for attendance bonus this month', 14, finalY);
        doc.setTextColor(0, 0, 0); // 恢復黑色
        doc.setFont('helvetica', 'normal');
      }
    }

    // 生成文件名 - 使用 user id 和 display name
    const sanitizeFileName = (str) => {
      if (!str) return '';
      // 移除或替換文件名中不允許的字符
      return str.replace(/[<>:"/\\|?*]/g, '_').trim();
    };
    
    const userId = selectedUser?.id || 'Unknown';
    const displayName = sanitizeFileName(selectedUser?.display_name || selectedUser?.name_zh || selectedUser?.name || 'User');
    const fileName = `MonthlySummary_${userId}_${displayName}.pdf`;
    
    // 保存 PDF
    doc.save(fileName);
    
    Swal.fire({
      icon: 'success',
      title: t('attendance.success') || '成功',
      text: t('attendance.exportPDFSuccess') || 'PDF 導出成功'
    });
    } catch (error) {
      console.error('Export PDF error:', error);
      Swal.fire({
        icon: 'error',
        title: t('attendance.error') || '錯誤',
        text: error.message || t('attendance.exportPDFFailed') || 'PDF 導出失敗'
      });
    }
  };

  // 當有排班時間及考勤情況時，前端自行計算遲到、Break、全日上班總、超時、應計超時
  const storesMap = useMemo(() => {
    const map = {};
    (stores || []).forEach(s => {
      const code = s.store_code || s.store_code_;
      const name = s.store_short_name_ ?? s.store_short_name;
      if (code != null) map[String(code).trim()] = name || '';
    });
    return map;
  }, [stores]);

  const dailyData = useMemo(() => {
    const raw = summary?.daily_data || [];
    if (!selectedUserId || raw.length === 0) return raw;
    const selectedUser = users.find(u => u.id === selectedUserId);
    const employmentMode = (selectedUser?.position_employment_mode ?? selectedUser?.employment_mode ?? '').toString().trim().toUpperCase();

    return raw.map(day => {
      const schedule = day.schedule || day.attendance_data?.schedule || null;
      const hasSchedule = schedule && (schedule.start_time || schedule.end_time || schedule.leave_type_name_zh);
      const rawClockRecords = day.attendance_data?.clock_records ?? [];
      const validOnly = day.valid_clock_records ?? [];
      const clockRecords = Array.isArray(rawClockRecords) && rawClockRecords.length > 0
        ? rawClockRecords
        : Array.isArray(validOnly) && validOnly.length > 0
          ? validOnly.map(r => ({ ...r, is_valid: true }))
          : [];
      const hasClockRecords = clockRecords.length > 0;

      if (!hasSchedule && !hasClockRecords) return day;
      // 有排班或考勤其一即可計算（例如只有考勤可算總工時，只有排班+考勤可算遲到/超時）
      const attendanceItem = {
        attendance_date: day.date,
        schedule: schedule,
        clock_records: clockRecords
      };
      const calculated = calculateDailyAttendance(attendanceItem, storesMap, employmentMode);
      return {
        ...day,
        late_minutes: calculated.late_minutes !== null ? calculated.late_minutes : day.late_minutes,
        break_duration: calculated.break_duration !== null ? calculated.break_duration : day.break_duration,
        total_work_hours: calculated.total_work_hours !== null ? calculated.total_work_hours : day.total_work_hours,
        overtime_hours: calculated.overtime_hours !== null ? calculated.overtime_hours : day.overtime_hours,
        approved_overtime_minutes: calculated.approved_overtime_minutes !== null ? calculated.approved_overtime_minutes : day.approved_overtime_minutes,
        store_short_name: calculated.store_short_name || day.store_short_name,
        valid_clock_records: (calculated.valid_clock_records?.length ? calculated.valid_clock_records : null) ?? day.valid_clock_records,
        early_leave: calculated.early_leave !== undefined ? calculated.early_leave : day.early_leave,
        is_late: calculated.is_late !== undefined ? calculated.is_late : day.is_late,
        is_absent: calculated.is_absent !== undefined ? calculated.is_absent : day.is_absent
      };
    });
  }, [summary, selectedUserId, users, storesMap]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={noLayout ? { mt: 0, mb: 0 } : { mt: 4, mb: 4 }}>
        <Paper 
          elevation={3}
          sx={{ 
            p: 4,
            borderRadius: 3,
            background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
          }}
        >
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                mb: 1,
              }}
            >
              {t('attendance.monthlySummary') || '月結表'}
            </Typography>
            {summary && selectedUserId && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExportPDF}
                color="primary"
                sx={{ mb: 1 }}
              >
                {t('attendance.exportPDF') || '導出 PDF'}
              </Button>
            )}
            <Divider sx={{ width: '100%', mt: 1 }} />
          </Box>

          <Card 
            elevation={2}
            sx={{ 
              mb: 3, 
              p: 3,
              borderRadius: 2,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => 
                    `${option.employee_number || ''} - ${option.display_name || option.name_zh || option.name || ''}`
                  }
                  value={users.find(u => u.id === selectedUserId) || null}
                  onChange={(event, newValue) => {
                    setSelectedUserId(newValue ? newValue.id : null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('attendance.selectEmployee') || '選擇員工'}
                      placeholder={t('attendance.selectEmployee') || '選擇員工'}
                    />
                  )}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const searchLower = inputValue.toLowerCase();
                    return options.filter((u) => {
                      const employeeNumber = (u.employee_number || '').toLowerCase();
                      const displayName = (u.display_name || '').toLowerCase();
                      const nameZh = (u.name_zh || '').toLowerCase();
                      const name = (u.name || '').toLowerCase();
                      const givenName = (u.given_name || '').toLowerCase();
                      const surname = (u.surname || '').toLowerCase();
                      const alias = (u.alias || '').toLowerCase();
                      
                      return (
                        employeeNumber.includes(searchLower) ||
                        displayName.includes(searchLower) ||
                        nameZh.includes(searchLower) ||
                        name.includes(searchLower) ||
                        givenName.includes(searchLower) ||
                        surname.includes(searchLower) ||
                        alias.includes(searchLower) ||
                        `${surname} ${givenName}`.trim().includes(searchLower) ||
                        `${givenName} ${surname}`.trim().includes(searchLower)
                      );
                    });
                  }}
                  noOptionsText={t('attendance.noUsersFound') || '找不到員工'}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
              <DatePicker
                label={t('attendance.startDate') || '開始日期'}
                value={selectedStartDate}
                onChange={handleStartDateChange}
                format="YYYY-MM-DD"
                maxDate={selectedEndDate || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { bgcolor: 'background.paper', borderRadius: 1 }
                  }
                }}
              />
              </Grid>
              <Grid item xs={12} md={4}>
              <DatePicker
                label={t('attendance.endDate') || '結束日期'}
                value={selectedEndDate}
                onChange={handleEndDateChange}
                format="YYYY-MM-DD"
                minDate={selectedStartDate || undefined}
                maxDate={selectedStartDate ? selectedStartDate.add(44, 'day') : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { bgcolor: 'background.paper', borderRadius: 1 }
                  }
                }}
              />
              </Grid>
            </Grid>
            
            {/* 統計信息 */}
            {summary && dailyData.length > 0 && (
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {(() => {
                        const selectedUser = users.find(u => u.id === selectedUserId);
                        const employmentMode = selectedUser?.position_employment_mode;
                        const mode = (employmentMode || '').toString().trim().toUpperCase();
                        return mode === 'FT' 
                          ? (t('attendance.approvedOvertimeHoursForFT') || '應計超時工作時數')
                          : (t('attendance.approvedOvertimeHours') || '應計工作時數');
                      })()}：
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1565C0' }}>
                      {(() => {
                        const totalMinutes = dailyData.reduce((sum, day) => {
                          return sum + (day.approved_overtime_minutes || 0);
                        }, 0);
                        return formatTime(totalMinutes);
                      })()}
                    </Typography>
                  </Grid>
                  {(() => {
                    const totalLate = dailyData.reduce((sum, day) => {
                      return sum + (day.late_minutes || 0);
                    }, 0);
                    // 只有總遲到 >= 10 分鐘時才顯示
                    if (totalLate < 10) return null;
                    return (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          {t('attendance.totalLateMinutes') || '總遲到分鐘數'}：
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600,
                            color: 'error.main'
                          }}
                        >
                          {totalLate} {t('attendance.minutes') || '分鐘'}
                        </Typography>
                      </Grid>
                    );
                  })()}
                </Grid>
                
                {/* 勤工獎資格檢查 - 僅對FT員工顯示 */}
                {(() => {
                  const selectedUser = users.find(u => u.id === selectedUserId);
                  const employmentMode = selectedUser?.position_employment_mode;
                  
                  // 只檢查FT員工
                  if (employmentMode && employmentMode.toString().trim().toUpperCase() === 'FT') {
                    // 計算總遲到時間
                    const totalLate = dailyData.reduce((sum, day) => {
                      return sum + (day.late_minutes || 0);
                    }, 0);
                    
                    // 定義不符合資格的假期類型
                    const disqualifiedLeaveTypes = [
                      '無薪事假',
                      '無薪病假',
                      '病假 (疾病津貼)',
                      '全薪病假',
                      '工傷病假',
                      '產假',
                      '侍產假',
                      '恩恤假'
                    ];
                    
                    // 檢查是否有不符合資格的假期
                    const hasDisqualifiedLeave = dailyData.some(day => {
                      const leaveType = day.schedule?.leave_type_name_zh || 
                                       day.attendance_data?.schedule?.leave_type_name_zh;
                      return leaveType && disqualifiedLeaveTypes.includes(leaveType);
                    });
                    
                    // 如果遲到 >= 10分鐘或有不符合資格的假期，顯示提示
                    if (totalLate >= 10 || hasDisqualifiedLeave) {
                      return (
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'error.main',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            {t('attendance.notEligibleForAttendanceBonus') || '本月不符合領取勤工獎資格'}
                          </Typography>
                        </Box>
                      );
                    }
                  }
                  return null;
                })()}
              </Box>
            )}

            {/* 生成月報按鈕 */}
            {canManageMonthly && summary && selectedUserId && (
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DescriptionIcon />}
                  onClick={openGenerateReportDialog}
                  disabled={generatingReport || !selectedUserId || !selectedStartDate || !selectedEndDate}
                  sx={{ minWidth: 200 }}
                >
                  {generatingReport ? t('attendance.generating') : t('attendance.generateReport')}
                </Button>
              </Box>
            )}
          </Card>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary">
                {t('common.loading') || '載入中...'}
              </Typography>
            </Box>
          ) : summary ? (
            <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {t('attendance.date') || '日期'}
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {t('attendance.roster') || '排班'}
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {t('attendance.store') || '分店'}
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {t('attendance.attendanceStatus') || '考勤情況'}
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {t('attendance.lateMinutes') || '遲到(分鐘)'}
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {t('attendance.breakDuration') || 'Break時間(分鐘)'}
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {t('attendance.totalWorkHours') || '全日上班總時數'}
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {t('attendance.overtimeHours') || '超時工作時間'}
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {(() => {
                          const selectedUser = users.find(u => u.id === selectedUserId);
                          const employmentMode = selectedUser?.position_employment_mode;
                          const mode = (employmentMode || '').toString().trim().toUpperCase();
                          return mode === 'FT' 
                            ? (t('attendance.approvedOvertimeHoursForFT') || '應計超時工作時數')
                            : (t('attendance.approvedOvertimeHours') || '應計工作時數');
                        })()}
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}>
                        {t('attendance.details') || '詳情'}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dailyData.map((day, index) => {
                      const isExpanded = expandedRows.has(day.date);
                      // 優先從 day.schedule 讀取，如果沒有則從 attendance_data.schedule 讀取
                      const schedule = day.schedule || day.attendance_data?.schedule || null;
                      
                      // 獲取有效的打卡記錄：確保所有有效的打卡時間都被 map 出來
                      let validRecords = [];
                      
                      // 方法1: 優先從 valid_clock_records 讀取（後端已過濾好的）
                      if (day.valid_clock_records && Array.isArray(day.valid_clock_records) && day.valid_clock_records.length > 0) {
                        validRecords = day.valid_clock_records;
                      }
                      
                      // 方法2: 如果 valid_clock_records 為空，從 attendance_data.clock_records 中過濾所有有效的記錄（僅用後端回傳的資料，不暫存、不額外請求）
                      if (validRecords.length === 0 && day.attendance_data?.clock_records && Array.isArray(day.attendance_data.clock_records)) {
                        validRecords = day.attendance_data.clock_records
                          .filter(r => {
                            const isValid = r.is_valid === true || 
                                          r.is_valid === 'true' || 
                                          r.is_valid === 1 || 
                                          r.is_valid === '1' ||
                                          r.is_valid === 'True' ||
                                          (typeof r.is_valid === 'string' && r.is_valid.toLowerCase() === 'true');
                            return isValid && r.clock_time;
                          })
                          .sort((a, b) => {
                            const timeA = a.clock_time || '';
                            const timeB = b.clock_time || '';
                            return timeA.localeCompare(timeB);
                          });
                      }

                      return (
                        <React.Fragment key={day.date || index}>
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Typography variant="body2" component="span">
                                  {day.date}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="span" sx={{ mt: 0.25 }}>
                                  {formatWeekdayShortParen(day.date, i18n.language)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {schedule ? (
                                <Box>
                                  {schedule.leave_type_name_zh ? (
                                    <Box sx={{ mb: 0.5 }}>
                                      <Chip
                                        label={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {schedule.leave_type_name_zh}
                                            </Typography>
                                            {schedule.leave_session && (
                                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                ({schedule.leave_session})
                                              </Typography>
                                            )}
                                          </Box>
                                        }
                                        size="small"
                                        color={schedule.is_approved_leave ? 'success' : 'warning'}
                                        sx={{ 
                                          display: 'inline-flex',
                                          fontWeight: 600,
                                          boxShadow: schedule.is_approved_leave 
                                            ? '0 2px 4px rgba(76, 175, 80, 0.3)' 
                                            : '0 2px 4px rgba(237, 108, 2, 0.3)'
                                        }}
                                      />
                                    </Box>
                                  ) : null}
                                  {(schedule.start_time || schedule.end_time) ? (
                                    <Box sx={{ mt: schedule.leave_type_name_zh ? 0.5 : 0 }}>
                                      <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                        {schedule.start_time ? formatTimeDisplay(schedule.start_time) : '--:--'} - {schedule.end_time ? formatTimeDisplay(schedule.end_time) : '--:--'}
                                      </Typography>
                                    </Box>
                                  ) : schedule.leave_type_name_zh ? null : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      {t('attendance.noScheduleTime') || '無排班時間'}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">--</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {day.store_short_name ? (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {day.store_short_name}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">--</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box>
                                {validRecords.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    {/* 顯示所有有效的打卡時間 - 同一行顯示（不顯示 in/out、branch_code/store_code） */}
                                    {validRecords.map((record, idx) => {
                                      // 確保每條記錄都有打卡時間
                                      if (!record.clock_time) {
                                        console.warn(`[MonthlyAttendanceSummary] Record at index ${idx} missing clock_time:`, record);
                                        return null;
                                      }
                                      return (
                                        <Typography
                                          key={record.id || `record-${idx}`}
                                          variant="body2"
                                          sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 700,
                                            color: 'primary.main',
                                            fontFamily: 'monospace',
                                            letterSpacing: '0.5px',
                                            display: 'inline-block'
                                          }}
                                        >
                                          {formatTimeDisplay(record.clock_time)}
                                          {idx < validRecords.length - 1 && (
                                            <span style={{ margin: '0 4px', color: '#999' }}>|</span>
                                          )}
                                        </Typography>
                                      );
                                    })}
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    {t('attendance.noRecords') || '無記錄'}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {day.late_minutes !== null ? (
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: 'error.main',
                                    display: 'inline-block'
                                  }}
                                >
                                  {formatTime(day.late_minutes)}
                                </Typography>
                              ) : (
                                '--'
                              )}
                            </TableCell>
                            <TableCell>{day.break_duration !== null ? formatTime(day.break_duration) : '--'}</TableCell>
                            <TableCell>{formatHoursAsTime(day.total_work_hours)}</TableCell>
                            <TableCell>{formatHoursAsTime(day.overtime_hours)}</TableCell>
                            <TableCell>
                              {day.approved_overtime_minutes !== null ? (
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: '#1565C0', // 深藍色
                                    display: 'inline-block'
                                  }}
                                >
                                  {formatTime(day.approved_overtime_minutes)}
                                </Typography>
                              ) : (
                                '--'
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetail(day)}
                                color="primary"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {isExpanded && validRecords.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ bgcolor: 'grey.50', py: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                  {t('attendance.validClockRecords') || '有效打卡記錄'}:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {validRecords.map((record, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: '0.875rem' }}>
                                      <Typography variant="body2">
                                        {formatTimeDisplay(record.clock_time)} ({record.in_out || '--'})
                                      </Typography>
                                      {record.branch_code && (
                                        <Typography variant="caption" color="text.secondary">
                                          [{record.branch_code}]
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    
                    {/* 總計行 */}
                    {dailyData.length > 0 && (() => {
                      const foot = computeMonthlySummaryFootStats(dailyData);

                      return (
                        <TableRow sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t('common.total') || '總計'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('attendance.monthlySummaryTotalOccurrences', { count: foot.schedule_day_count })}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('attendance.monthlySummaryTotalOccurrences', { count: foot.store_day_count })}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('attendance.monthlySummaryTotalOccurrences', { count: foot.attendance_day_count })}
                          </TableCell>
                          <TableCell>
                            {foot.late_minutes > 0 ? (
                              <Box
                                component="span"
                                sx={{ fontWeight: 700, color: 'error.main', display: 'inline-block' }}
                              >
                                {formatTime(foot.late_minutes)}
                                {foot.late_occurrence_count > 0 ? (
                                  <>
                                    {' '}
                                    {t('attendance.monthlySummaryLateTimesInParens', {
                                      count: foot.late_occurrence_count
                                    })}
                                  </>
                                ) : null}
                              </Box>
                            ) : (
                              '--'
                            )}
                          </TableCell>
                          <TableCell>
                            {foot.break_duration > 0 ? formatTime(foot.break_duration) : '--'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {formatHoursAsTime(foot.total_work_hours)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {formatHoursAsTime(foot.overtime_hours)}
                          </TableCell>
                          <TableCell>
                            {foot.approved_overtime_minutes > 0 ? (
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: '#1565C0',
                                  display: 'inline-block'
                                }}
                              >
                                {formatTime(foot.approved_overtime_minutes)}
                              </Typography>
                            ) : (
                              '--'
                            )}
                          </TableCell>
                          <TableCell>--</TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ) : (
            <Card 
              elevation={2}
              sx={{ 
                textAlign: 'center', 
                py: 6,
                borderRadius: 2,
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                {t('attendance.noMonthlySummary') || '沒有月結記錄'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 480, mx: 'auto' }}>
                {(() => {
                  const hint = t('attendance.noMonthlySummaryHint');
                  if (!hint) {
                    return '選擇員工與日期區間後，系統會自動從排班與打卡即時計算遲到、Break 時間、全日上班總時數、超時工作及應計超時工作時數，無需先生成月報。若仍無資料，請確認該員工已分配部門群組。';
                  }
                  // 原本文字包含「年份與月份」：改為「日期區間」以符合本頁需求
                  return hint.replace('年份與月份', '日期區間').replace('年份、年份與月份', '日期區間');
                })()}
              </Typography>
            </Card>
          )}

          {/* 生成月報：選擇年份／月份 */}
          <Dialog
            open={generateReportDialogOpen}
            onClose={() => !generatingReport && setGenerateReportDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{t('attendance.selectReportYearMonthTitle')}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="generate-report-year-label">{t('attendance.reportYear')}</InputLabel>
                    <Select
                      labelId="generate-report-year-label"
                      label={t('attendance.reportYear')}
                      value={dialogReportYear}
                      onChange={(e) => setDialogReportYear(Number(e.target.value))}
                    >
                      {reportYearOptions.map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="generate-report-month-label">{t('attendance.reportMonth')}</InputLabel>
                    <Select
                      labelId="generate-report-month-label"
                      label={t('attendance.reportMonth')}
                      value={dialogReportMonth}
                      onChange={(e) => setDialogReportMonth(Number(e.target.value))}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setGenerateReportDialogOpen(false)} disabled={generatingReport}>
                {t('common.cancel')}
              </Button>
              <Button variant="contained" onClick={handleConfirmGenerateReportDialog} disabled={generatingReport}>
                {t('attendance.confirmGenerateReport')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* 詳情對話框 */}
          <Dialog
            open={detailDialogOpen}
            onClose={() => {
              setDetailDialogOpen(false);
              setSelectedDayDetail(null);
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              {t('attendance.details') || '詳情'} - {selectedDayDetail?.date}
            </DialogTitle>
            <DialogContent>
              {selectedDayDetail && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  {/* 排班資料 */}
                  {(selectedDayDetail.schedule || selectedDayDetail.attendance_data?.schedule) && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('attendance.roster') || '排班資料'}
                      </Typography>
                      {(() => {
                        const schedule = selectedDayDetail.schedule || selectedDayDetail.attendance_data?.schedule;
                        return (
                          <Grid container spacing={2}>
                            {schedule.start_time && (
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('attendance.startTime') || '開始時間'}:
                                </Typography>
                                <Typography variant="body1">
                                  {formatTimeDisplay(schedule.start_time)}
                                </Typography>
                              </Grid>
                            )}
                            {schedule.end_time && (
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('attendance.endTime') || '結束時間'}:
                                </Typography>
                                <Typography variant="body1">
                                  {formatTimeDisplay(schedule.end_time)}
                                </Typography>
                              </Grid>
                            )}
                            {schedule.leave_type_name_zh && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  {t('attendance.leaveType') || '假期類型'}:
                                </Typography>
                                <Chip
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {schedule.leave_type_name_zh}
                                      </Typography>
                                      {schedule.leave_session && (
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                          ({schedule.leave_session})
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  color={schedule.is_approved_leave ? 'success' : 'warning'}
                                  size="medium"
                                  sx={{ 
                                    fontWeight: 600,
                                    boxShadow: schedule.is_approved_leave 
                                      ? '0 2px 6px rgba(76, 175, 80, 0.3)' 
                                      : '0 2px 6px rgba(237, 108, 2, 0.3)'
                                  }}
                                />
                              </Grid>
                            )}
                            {!schedule.start_time && !schedule.end_time && !schedule.leave_type_name_zh && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('attendance.noSchedule') || '無排班資料'}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        );
                      })()}
                    </Box>
                  )}

                  {/* 所有打卡記錄 */}
                  {selectedDayDetail.attendance_data?.clock_records && selectedDayDetail.attendance_data.clock_records.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('attendance.allClockRecords') || '所有打卡記錄'} ({selectedDayDetail.attendance_data.clock_records.length})
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('attendance.clockTime') || '打卡時間'}</TableCell>
                              <TableCell>{t('attendance.inOut') || '進出'}</TableCell>
                              <TableCell>{t('attendance.branchCode') || '分行代碼'}</TableCell>
                              <TableCell>{t('attendance.isValid') || '有效'}</TableCell>
                              <TableCell>{t('attendance.remarks') || '備註'}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedDayDetail.attendance_data.clock_records.map((record, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{formatTimeDisplay(record.clock_time)}</TableCell>
                                <TableCell>{record.in_out || '--'}</TableCell>
                                <TableCell>{record.branch_code || '--'}</TableCell>
                                <TableCell>
                                  {record.is_valid ? (
                                    <Chip label={t('common.yes') || '是'} size="small" color="success" />
                                  ) : (
                                    <Chip label={t('common.no') || '否'} size="small" />
                                  )}
                                </TableCell>
                                <TableCell>{record.remarks || '--'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setDetailDialogOpen(false);
                setSelectedDayDetail(null);
              }}>
                {t('common.close') || '關閉'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default MonthlyAttendanceSummary;
